import { useCart } from '@hooks/useCart/useCart';
import { useAddChildItem } from '@hooks/useAddChildItem/useAddChildItem';
import { useDeleteChildItems } from '@hooks/useDeleteChildItem/useDeleteChildItem';
import type { CartItemDTO, ShoppingCartDTO } from '@t/api.types';
import type { PlanAddOn } from '@t/productOffering.types';
import type { AddOnActions, AddOnChildInput, UseAddOnActionsOptions } from './useAddOnActions.types';

const buildChildItemPayload = (item: AddOnChildInput): CartItemDTO => ({
  action: 'add',
  quantity: item.quantity,
  productOffering: { id: item.id, name: item.name },
});

export const useAddOnActions = (sessionId: string, { onError }: UseAddOnActionsOptions = {}): AddOnActions => {
  const cartQuery = useCart(sessionId);
  const addChildM = useAddChildItem(sessionId, { onError });
  const deleteChildM = useDeleteChildItems(sessionId, { onError });

  const selectAddOn = (parentOfferingCode: string, addOn: PlanAddOn) => {
    addChildM.mutate({
      parentOfferingCode,
      items: [buildChildItemPayload({ id: addOn.id, name: addOn.name, quantity: addOn.quantity ?? 1 })],
    });
  };

  const selectAddOnAsync = (parentOfferingCode: string, addOn: PlanAddOn) =>
    addChildM.mutateAsync({
      parentOfferingCode,
      items: [buildChildItemPayload({ id: addOn.id, name: addOn.name, quantity: addOn.quantity ?? 1 })],
    });

  const selectAddOnsAsync = async (parentOfferingCode: string, items: AddOnChildInput[]): Promise<ShoppingCartDTO> => {
    const payloads = items.filter((i) => i.quantity > 0).map(buildChildItemPayload);
    if (payloads.length === 0) {
      return cartQuery.data ?? ({} as ShoppingCartDTO);
    }
    return addChildM.mutateAsync({ parentOfferingCode, items: payloads });
  };

  const removeAddOn = (childItemId: string) => {
    deleteChildM.mutate([childItemId]);
  };

  const removeAddOnAsync = (childItemId: string) => deleteChildM.mutateAsync([childItemId]);

  const removeAddOnsAsync = async (childItemIds: string[]): Promise<ShoppingCartDTO> => {
    if (childItemIds.length === 0) {
      return cartQuery.data ?? ({} as ShoppingCartDTO);
    }
    return deleteChildM.mutateAsync(childItemIds);
  };

  return {
    selectAddOn,
    selectAddOnAsync,
    selectAddOnsAsync,
    removeAddOn,
    removeAddOnAsync,
    removeAddOnsAsync,
    addChildItemAsync: addChildM.mutateAsync,
    isPending: addChildM.isPending || deleteChildM.isPending,
  };
};
